import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { fetchStatusForProduct } from './middleware/productStatusMiddleware';
import { normalizeStatus, getOverallStatus, getStatusColorClass, formatTimestamp } from './utils/statusUtils';

const PRODUCT_NAMES = ["CXP", "ASAP", "VancoEvents"];
const AUTO_REFRESH_INTERVAL = 20000; // 20 seconds

function App() {
  const [services, setServices] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('CXP');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchStatusForProduct(selectedProduct);
      setServices(Array.isArray(data) ? data : []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching status:', err);
      setError(err.message || 'Failed to load status data. Please try again.');
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProduct]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchStatus();
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchStatus]);

  const handleProductChange = (e) => {
    const productName = e.target.value;
    setSelectedProduct(productName);
    setServices([]);
  };

  const handleRetry = () => {
    fetchStatus();
  };

  const overallStatus = getOverallStatus(services);
  const hasServices = services.length > 0;

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <h1>Vanco Status</h1>
          <div className="header-controls">
            <div className="product-selector">
              <label htmlFor="product">Product: </label>
              <select 
                id="product" 
                onChange={handleProductChange} 
                value={selectedProduct}
                disabled={loading}
              >
                {PRODUCT_NAMES.map(productName => (
                  <option key={productName} value={productName}>{productName}</option>
                ))}
              </select>
            </div>
            <label className="auto-refresh-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <span>Auto-refresh (20s)</span>
            </label>
          </div>
        </div>
        <div className="status-summary">
          <p className={`overall-status status-${overallStatus.toLowerCase().replace(/\s+/g, '-')}`}>
            {overallStatus}
          </p>
          {lastUpdated && (
            <p className="last-updated">
              Last updated: {formatTimestamp(lastUpdated)}
            </p>
          )}
        </div>
      </header>

      {error && (
        <div className="message-banner error">
          <span>{error}</span>
          <button onClick={handleRetry} className="retry-button">Retry</button>
        </div>
      )}

      <main>
        <section className="service-status">
          <div className="section-header">
            <h2>Service Status</h2>
            {loading && <span className="loading-indicator">Refreshing...</span>}
          </div>
          
          {loading && !hasServices ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading service status...</p>
            </div>
          ) : !hasServices ? (
            <div className="empty-state">
              <p>No services available for {selectedProduct}.</p>
            </div>
          ) : (
            <ul className="service-list">
              {services
                .filter(service => service && service.name)
                .map((service, index) => {
                  const normalizedStatus = normalizeStatus(service.status);
                  const statusClass = getStatusColorClass(normalizedStatus);
                  
                  return (
                    <li key={service.name || index} className={`service-item ${statusClass}`}>
                      <div className="service-info">
                        <span className="service-name">{service.name}</span>
                        <span className={`status-badge ${statusClass}`}>
                          {normalizedStatus}
                        </span>
                      </div>
                      {service.error && (
                        <span className="service-error" style={{ 
                          fontSize: '0.85rem', 
                          color: service.corsBlocked ? '#ff9800' : '#d73a49',
                          marginTop: '0.5rem',
                          display: 'block'
                        }}>
                          {service.corsBlocked ? '⚠ CORS blocked - Status cannot be verified' : service.error}
                        </span>
                      )}
                      {service.lastChecked && (
                        <span className="service-meta">
                          Checked: {formatTimestamp(service.lastChecked)}
                        </span>
                      )}
                    </li>
                  );
                })}
            </ul>
          )}
        </section>

        <section className="incident-history">
          <h2>Incident History</h2>
          <div className="empty-state">
            <p>No incidents reported.</p>
            <small>Incident history will appear here when available.</small>
          </div>
        </section>
      </main>
    </div>
  );
}

export default App;